import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Settings as SettingsIcon, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ user }) => {
  const [settings, setSettings] = useState({
    // Management Company Details
    company_name: 'Underplay Music',
    vat_number: '',
    company_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    email: '',
    phone: '',
    bank_name: '',
    account_number: '',
    sort_code: '',
    iban: '',
    swift_code: '',
    payment_notes: '',
    default_currency: 'GBP',
    // Record Label Details
    label_company_name: '',
    label_vat_number: '',
    label_company_number: '',
    label_address_line1: '',
    label_address_line2: '',
    label_city: '',
    label_postcode: '',
    label_country: 'United Kingdom',
    label_email: '',
    label_phone: '',
    label_bank_name: '',
    label_account_number: '',
    label_sort_code: '',
    label_iban: '',
    label_swift_code: '',
    label_payment_notes: '',
  });
  
  const [activeTab, setActiveTab] = useState('management'); // 'management' or 'label'
  
  const CURRENCIES = [
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
    { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
    { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  ];
  const [loading, setLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings, using defaults:', error.message);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved successfully!');
    } catch (error) { // Silent fail - use defaults
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Invoice Settings</h1>
          <p className="text-muted-foreground text-base mt-2">
            Configure company details for invoices
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'management' ? 'default' : 'outline'}
          onClick={() => setActiveTab('management')}
          className="flex-1 sm:flex-none"
          data-testid="management-tab"
        >
          Management Details
        </Button>
        <Button
          variant={activeTab === 'label' ? 'default' : 'outline'}
          onClick={() => setActiveTab('label')}
          className="flex-1 sm:flex-none"
          data-testid="label-tab"
        >
          Record Label Details
        </Button>
      </div>

      {/* Management Tab */}
      {activeTab === 'management' && (
      <div className="grid gap-6">
        {/* Company Information */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Management Company</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                data-testid="company-name-input"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vat_number">VAT/Tax Number</Label>
              <Input
                id="vat_number"
                data-testid="vat-number-input"
                placeholder="GB123456789"
                value={settings.vat_number}
                onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_number">Company Number</Label>
              <Input
                id="company_number"
                data-testid="company-number-input"
                placeholder="12345678"
                value={settings.company_number}
                onChange={(e) => setSettings({ ...settings, company_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Company Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="company-email-input"
                placeholder="info@underplaymusic.com"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Company Phone</Label>
              <Input
                id="phone"
                data-testid="company-phone-input"
                placeholder="+44 20 1234 5678"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_currency">Default Currency</Label>
              <select
                id="default_currency"
                data-testid="default-currency-select"
                value={settings.default_currency}
                onChange={(e) => setSettings({ ...settings, default_currency: e.target.value })}
                className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">All financial reports will consolidate to this currency</p>
            </div>
          </div>
        </Card>

        {/* Address */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Company Address</h2>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                data-testid="address-line1-input"
                placeholder="123 Music Street"
                value={settings.address_line1}
                onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
              <Input
                id="address_line2"
                data-testid="address-line2-input"
                placeholder="Suite 100"
                value={settings.address_line2}
                onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  placeholder="London"
                  value={settings.city}
                  onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode</Label>
                <Input
                  id="postcode"
                  data-testid="postcode-input"
                  placeholder="SW1A 1AA"
                  value={settings.postcode}
                  onChange={(e) => setSettings({ ...settings, postcode: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  data-testid="country-input"
                  value={settings.country}
                  onChange={(e) => setSettings({ ...settings, country: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Details */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                data-testid="bank-name-input"
                placeholder="Barclays Bank"
                value={settings.bank_name}
                onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_code">Sort Code</Label>
              <Input
                id="sort_code"
                data-testid="sort-code-input"
                placeholder="12-34-56"
                value={settings.sort_code}
                onChange={(e) => setSettings({ ...settings, sort_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="account_number">Account Number</Label>
              <Input
                id="account_number"
                data-testid="account-number-input"
                placeholder="12345678"
                value={settings.account_number}
                onChange={(e) => setSettings({ ...settings, account_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                data-testid="iban-input"
                placeholder="GB29 NWBK 6016 1331 9268 19"
                value={settings.iban}
                onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="swift_code">SWIFT Code</Label>
              <Input
                id="swift_code"
                data-testid="swift-code-input"
                placeholder="BARCGB22XXX"
                value={settings.swift_code}
                onChange={(e) => setSettings({ ...settings, swift_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="payment_notes">Payment Notes (Optional)</Label>
              <Textarea
                id="payment_notes"
                data-testid="payment-notes-input"
                placeholder="Please reference invoice number when making payment..."
                value={settings.payment_notes}
                onChange={(e) => setSettings({ ...settings, payment_notes: e.target.value })}
                className="bg-input/50 border-border rounded-xl min-h-24"
              />
            </div>
          </div>
        </Card>
      </div>
      )}

      {/* Record Label Tab */}
      {activeTab === 'label' && (
      <div className="grid gap-6">
        {/* Label Company Information */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Record Label Company</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="label_company_name">Company Name</Label>
              <Input
                id="label_company_name"
                data-testid="label-company-name-input"
                placeholder="Record Label Name"
                value={settings.label_company_name}
                onChange={(e) => setSettings({ ...settings, label_company_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_vat_number">VAT/Tax Number</Label>
              <Input
                id="label_vat_number"
                data-testid="label-vat-number-input"
                placeholder="GB123456789"
                value={settings.label_vat_number}
                onChange={(e) => setSettings({ ...settings, label_vat_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_company_number">Company Number</Label>
              <Input
                id="label_company_number"
                data-testid="label-company-number-input"
                placeholder="12345678"
                value={settings.label_company_number}
                onChange={(e) => setSettings({ ...settings, label_company_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_email">Company Email</Label>
              <Input
                id="label_email"
                type="email"
                data-testid="label-email-input"
                placeholder="label@underplaymusic.com"
                value={settings.label_email}
                onChange={(e) => setSettings({ ...settings, label_email: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_phone">Company Phone</Label>
              <Input
                id="label_phone"
                data-testid="label-phone-input"
                placeholder="+44 20 1234 5678"
                value={settings.label_phone}
                onChange={(e) => setSettings({ ...settings, label_phone: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
          </div>
        </Card>

        {/* Label Address */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Label Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="label_address_line1">Address Line 1</Label>
              <Input
                id="label_address_line1"
                data-testid="label-address-line1-input"
                placeholder="123 Record Street"
                value={settings.label_address_line1}
                onChange={(e) => setSettings({ ...settings, label_address_line1: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="label_address_line2">Address Line 2</Label>
              <Input
                id="label_address_line2"
                data-testid="label-address-line2-input"
                placeholder="Suite 100"
                value={settings.label_address_line2}
                onChange={(e) => setSettings({ ...settings, label_address_line2: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_city">City</Label>
              <Input
                id="label_city"
                data-testid="label-city-input"
                placeholder="London"
                value={settings.label_city}
                onChange={(e) => setSettings({ ...settings, label_city: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_postcode">Postcode</Label>
              <Input
                id="label_postcode"
                data-testid="label-postcode-input"
                placeholder="SW1A 1AA"
                value={settings.label_postcode}
                onChange={(e) => setSettings({ ...settings, label_postcode: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_country">Country</Label>
              <Input
                id="label_country"
                data-testid="label-country-input"
                placeholder="United Kingdom"
                value={settings.label_country}
                onChange={(e) => setSettings({ ...settings, label_country: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
          </div>
        </Card>

        {/* Label Bank Details */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Label Bank Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="label_bank_name">Bank Name</Label>
              <Input
                id="label_bank_name"
                data-testid="label-bank-name-input"
                placeholder="Barclays"
                value={settings.label_bank_name}
                onChange={(e) => setSettings({ ...settings, label_bank_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_sort_code">Sort Code</Label>
              <Input
                id="label_sort_code"
                data-testid="label-sort-code-input"
                placeholder="20-00-00"
                value={settings.label_sort_code}
                onChange={(e) => setSettings({ ...settings, label_sort_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_account_number">Account Number</Label>
              <Input
                id="label_account_number"
                data-testid="label-account-number-input"
                placeholder="12345678"
                value={settings.label_account_number}
                onChange={(e) => setSettings({ ...settings, label_account_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_iban">IBAN</Label>
              <Input
                id="label_iban"
                data-testid="label-iban-input"
                placeholder="GB29 NWBK 6016 1331 9268 19"
                value={settings.label_iban}
                onChange={(e) => setSettings({ ...settings, label_iban: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label_swift_code">SWIFT Code</Label>
              <Input
                id="label_swift_code"
                data-testid="label-swift-code-input"
                placeholder="BARCGB22XXX"
                value={settings.label_swift_code}
                onChange={(e) => setSettings({ ...settings, label_swift_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="label_payment_notes">Payment Notes (Optional)</Label>
              <Textarea
                id="label_payment_notes"
                data-testid="label-payment-notes-input"
                placeholder="Please reference invoice number when making payment..."
                value={settings.label_payment_notes}
                onChange={(e) => setSettings({ ...settings, label_payment_notes: e.target.value })}
                className="bg-input/50 border-border rounded-xl min-h-24"
              />
            </div>
          </div>
        </Card>
      </div>
      )}

        {/* SendGrid Setup Instructions */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-4">Email Setup (SendGrid)</h2>
          <p className="text-sm text-muted-foreground mb-4">
            SendGrid lets you email invoices to clients. It&apos;s free for 100 emails/day.
          </p>
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-foreground">Quick Setup (2 minutes):</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Go to <a href="https://signup.sendgrid.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">signup.sendgrid.com</a></li>
              <li>Create free account (no credit card needed)</li>
              <li>Go to Settings → API Keys → Create API Key</li>
              <li>Copy the key and email it to your developer/support</li>
              <li>They&apos;ll add it to your backend in 1 minute</li>
            </ol>
            <p className="text-xs text-muted-foreground mt-4">
              Once configured, the &quot;Send Email&quot; button will work automatically!
            </p>
          </div>
        </Card>
    </div>
  );
};

export default Settings;